type CacheEntry = {
    promise: Promise<string>;
    refCount: number;
};

const cache = new Map<string, CacheEntry>();

const buildCacheKey = (imageType: string, sourceUrl: string) => `${imageType}:${sourceUrl}`;

const revokeIfObjectUrl = (url: string) => {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
};

const releaseEntry = (cacheKey: string, entry: CacheEntry) => {
    entry.refCount -= 1;
    if (entry.refCount > 0) return;

    // Only delete the entry we were holding — a newer load may already have replaced it under this key.
    if (cache.get(cacheKey) === entry) cache.delete(cacheKey);
    entry.promise.then(revokeIfObjectUrl).catch(() => undefined);
};

export type DecodedImageHandle = {
    /** Resolves to the decoded, displayable image URL (may be an object URL or the original source URL). */
    promise: Promise<string>;
    /** Releases this holder's reference. Idempotent — safe to call more than once. */
    release: () => void;
};

/**
 * Modal-scoped, ref-counted cache of decoded editor images, shared across every mount that acquires it.
 *
 * Keyed by `imageType:sourceUrl`, so a crop editor's prefetch of an alternate source and a later
 * Background-mode preview of that same source share one in-flight load and one decoded object URL instead
 * of loading and decoding it twice. Concurrent acquisitions for the same key dedupe onto the same promise
 * (`load` is only invoked on a cache miss).
 *
 * The underlying object URL is only revoked once every holder — including a prefetch that is released
 * unused before anything else acquired it — has released its handle. A failed load evicts its entry
 * immediately (via the `load` promise's own rejection) so a later acquisition retries instead of
 * replaying the failure forever.
 */
export const acquireDecodedImage = (
    sourceUrl: string,
    imageType: string,
    load: () => Promise<string>,
): DecodedImageHandle => {
    const cacheKey = buildCacheKey(imageType, sourceUrl);
    let entry = cache.get(cacheKey);

    if (!entry) {
        const promise = load();
        entry = { promise, refCount: 0 };
        cache.set(cacheKey, entry);
        promise.catch(() => {
            if (cache.get(cacheKey) === entry) cache.delete(cacheKey);
        });
    }

    entry.refCount += 1;
    let released = false;

    return {
        promise: entry.promise,
        release: () => {
            if (released) return;
            released = true;
            releaseEntry(cacheKey, entry as CacheEntry);
        },
    };
};
